import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
// The same badges the app itself shows, so a plan or a founding member reads
// identically here and in the product. Never restyled locally.
import PlanBadge from '../components/PlanBadge';
import { AdminNav, AdminLogin } from '../components/AdminChrome';
import FoundingBadge from '../components/FoundingBadge';
import { formatDate } from '../lib/date';

// Admin dashboard: the numbers that answer "what is actually happening" without
// anyone having to query Mongo by hand, or open PostHog, Resend and ElevenLabs
// in three tabs to find out what broke, what went out and what it cost.
//
// Two calls, deliberately: /api/admin/stats (the business numbers) and
// /api/admin/ops (errors, emails, spend). One being slow or broken leaves the
// other on screen.

const Stat = ({ label, value, sub, tone = 'default', href, onClick, selected, hint }) => {
    const tile = (
        <div className={`rounded-xl border px-4 py-3 h-full ${
            tone === 'good' ? 'border-emerald-200 bg-emerald-50'
                : tone === 'warn' ? 'border-amber-200 bg-amber-50'
                    : tone === 'bad' ? 'border-rose-200 bg-rose-50'
                        : 'border-stone-200 bg-white'
        }`}>
            <p className="text-xs uppercase tracking-wide text-stone-500">{label}</p>
            <p className="text-2xl font-bold text-foreground leading-tight mt-0.5">{value}</p>
            {sub && <p className="text-xs text-stone-500 mt-0.5">{sub}</p>}
            {href && <p className="text-xs text-primary mt-1">See the detail →</p>}
            {/* hint={false}: tappable, but the box is small enough to say nothing. */}
            {onClick && hint !== false && <p className="text-xs text-primary mt-1">{hint || 'See the detail →'}</p>}
        </div>
    );
    if (href) return <a href={href} className="block hover:opacity-80 transition-opacity">{tile}</a>;
    if (onClick) {
        return (
            <button type="button" onClick={onClick}
                    className={`text-left w-full rounded-xl transition-opacity hover:opacity-80 ${
                        selected ? 'ring-2 ring-primary' : ''}`}>
                {tile}
            </button>
        );
    }
    return tile;
};

// One heading and its content. Nothing folds any more: each tab holds only
// what belongs together, so there is nothing to open before it can be read.
const Section = ({ title, children, note, id }) => (
    <section id={id || title} className="mt-6 scroll-mt-6">
        <h2 className="text-sm font-bold uppercase tracking-wide text-stone-500 py-1">{title}</h2>
        <div className="mt-1">
            {note && <p className="text-xs text-stone-400 mb-3">{note}</p>}
            {children}
        </div>
    </section>
);

// The dashboard's pages. Each section name maps to the tab it lives on, so a
// box on the Overview can send you to the right one.
const TABS = [
    ['overview', 'Overview'],
    ['users', 'Users'],
    ['challenge', '30-day challenge'],
    ['money', 'Money'],
    ['health', 'Errors & email'],
];
const SECTION_TAB = {
    glance: 'overview',
    users: 'users', funnel: 'users',
    challenge: 'challenge',
    money: 'money', costs: 'money', cancellations: 'money',
    errors: 'health', emails: 'health', mailerlite: 'health',
};

// The one time switch. The same three windows the server counts in
// /api/admin/periods, worked out the same way here so a box and the list
// behind it always agree: since midnight UTC, plus 6 or 29 days before.
const PERIODS = [['today', 'Today', 0], ['week', '7 days', 6], ['month', '30 days', 29]];
const periodStart = (key) => {
    const days = (PERIODS.find(p => p[0] === key) || PERIODS[0])[2];
    const d = new Date();
    d.setUTCHours(0, 0, 0, 0);
    d.setUTCDate(d.getUTCDate() - days);
    return d.toISOString().slice(0, 10);
};
const periodLabel = (key) => (PERIODS.find(p => p[0] === key) || PERIODS[0])[1];

// Pill buttons in a row, one of them chosen. Used for the tabs' time switch.
const Segmented = ({ options, value, onChange }) => (
    <div className="inline-flex rounded-xl border border-stone-200 bg-white p-0.5">
        {options.map(([key, label]) => (
            <button key={key} type="button" onClick={() => onChange(key)}
                    className={`rounded-lg px-3 py-1 text-sm font-medium ${
                        value === key ? 'bg-primary text-white' : 'text-stone-600 hover:bg-stone-50'}`}>
                {label}
            </button>
        ))}
    </div>
);

// One bordered list, used by every table on the page so they all match.
const List = ({ children }) => (
    <div className="rounded-xl border border-stone-200 bg-white divide-y divide-stone-100 text-sm">
        {children}
    </div>
);

const Row = ({ left, right, sub, tone }) => (
    <div className="px-4 py-2 flex items-start justify-between gap-3">
        <div className="min-w-0">
            <p className={`truncate ${tone === 'bad' ? 'text-rose-600 font-medium' : 'text-foreground'}`}>{left}</p>
            {sub && <p className="text-xs text-stone-500 break-words">{sub}</p>}
        </div>
        <span className="text-xs text-stone-500 shrink-0 text-right whitespace-pre-line">{right}</span>
    </div>
);

// A row that opens to show what the error means and what to do about it.
const ExpandableRow = ({ title, right, sub, tone, children }) => {
    const [open, setOpen] = useState(false);
    return (
        <div className="px-4 py-2">
            <button type="button" onClick={() => setOpen(!open)}
                    className="w-full text-left flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <p className={`truncate ${tone === 'bad' ? 'text-rose-600 font-medium' : 'text-foreground'}`}>
                        <span className="text-stone-400 mr-1">{open ? '▾' : '▸'}</span>{title}
                    </p>
                    {sub && <p className="text-xs text-stone-500 truncate">{sub}</p>}
                </div>
                <span className="text-xs text-stone-500 shrink-0 text-right whitespace-pre-line">{right}</span>
            </button>
            {open && <div className="mt-2 mb-1 text-xs space-y-2">{children}</div>}
        </div>
    );
};

// Plain-English cause and fix for every error the app raises by name. Anything
// not listed falls back to the generic line: better than a wrong instruction.
const ERROR_HELP = {
    admin_test_error: 'The test button firing. Nothing is wrong.',
    gemini_failed: 'A call to Gemini failed, so whatever it was doing (a translation, phonetics, AI phrases) fell over for that user. A 429 in the message means the quota is spent: check Google AI Studio. A 503 is Google being busy and the retry usually covers it. Anything else, read the message, it comes straight from Google.',
    speechace_liaison: 'Not a fault. A real attempt where correct liaison was marked down and the app gave the marks back. The request_id below is what Speechace support asked for: quote it, with the phrase and the linked words, in the open case. The fault does not reproduce on synthetic French, so these live ones are the only evidence there is.',
    speechace_failed: 'Accuracy scoring failed, so that recording was scored by Gemini alone. The user still got a score. If it keeps happening, check the SpeechAce key and the account balance.',
    tts_provider_failed: 'ElevenLabs would not generate audio, so the app fell back to the browser voice. Usually the key or the credit balance. Check ElevenLabs usage.',
    email_send_failed: 'Resend rejected the send. Check the Resend dashboard for the reason: a bounced address, a domain problem, or the monthly limit.',
    reset_email_failed: 'Someone asked for a password reset and the email did not go. They are stuck out of their account until it does. Check Resend, then send them a link by hand.',
    paid_welcome_email_failed: 'Someone paid and did not get their set-password email. They have been charged and cannot get in. Fix this one first: check Resend, then email them a reset link.',
    reminder_email_failed: 'A daily practice reminder did not reach one user. Harmless on its own, worth checking Resend if several fail the same morning.',
    owner_alert_failed: 'An alert email to you failed to send. You may have missed a new subscriber or a cancellation. Check Resend.',
    mailerlite_add_failed: 'A new sign-up never reached the mailing list. Add them by hand in MailerLite, then check MAILERLITE_GROUP_ID in Railway.',
    stripe_no_user_for_customer: 'Stripe cancelled a subscription for a customer with no matching Mimique account. Find them in Stripe by the customer id below and sort the account by hand.',
    mic_denied: 'The user blocked the microphone in their browser. Nothing to fix in the app: the Help page covers turning it back on.',
    mic_missing: 'The browser found no microphone. Usually a device or a permissions problem at their end.',
    mic_unsupported: 'Their browser cannot record. Usually an in-app browser, for example opening the link inside Instagram or Facebook.',
    mic_failed: 'The browser could not open the microphone. If one user hits this repeatedly, ask what device and browser they are on.',
    recording_silent: 'A recording came back with no speech in it, scored 0. One now and then is someone tapping record and not speaking. Several in a row for the same person, after a first recording that scored, is the mic recording silence: send Claude the email and look into it.',
    analysis_failed: 'A recording failed to score. Look for a backend error at the same time, that is where the cause will be.',
    tts_failed: 'Audio would not play in their browser. Check for a tts_provider_failed at the same time: if there is one, it is us. If the row says their session had gone, that is the cause, their calls were all being refused and they need to sign in again. Otherwise it is their device.',
    tts_natural_voice_fallback: 'A paying user heard the browser voice instead of the natural one, which is the thing they are paying for. If it repeats, check ElevenLabs.',
    _default: 'No specific guidance for this one yet. The message and details below come straight from the source.',
};

const Help = ({ name }) => (
    <p className="text-stone-600">
        <span className="font-semibold">What to do: </span>
        {ERROR_HELP[name] || ERROR_HELP._default}
    </p>
);

// Where to go for the provider's own numbers.
const PROVIDER_LINKS = {
    gemini: ['Google AI Studio', 'https://aistudio.google.com/'],
    elevenlabs: ['ElevenLabs usage', 'https://elevenlabs.io/app/usage'],
    speechace: ['SpeechAce', 'https://www.speechace.com/'],
    resend: ['Resend', 'https://resend.com/emails'],
};

const ProviderLink = ({ service }) => {
    const link = PROVIDER_LINKS[service];
    if (!link) return null;
    return (
        <a href={link[1]} target="_blank" rel="noreferrer"
           className="text-primary underline" onClick={e => e.stopPropagation()}>
            {link[0]} ↗
        </a>
    );
};

// The people table. Sorting, searching and filtering all happen here, on data
// the page already has, so none of it costs a request.
const COLUMNS = [
    // Name and email share a column, email underneath, so the table fits the
    // page without scrolling sideways.
    { key: 'first_name', label: 'Person', align: 'left' },
    // The free-upgrade control sits under the plan, not in its own column.
    { key: 'plan', label: 'Plan', align: 'left', hint: 'and free upgrade' },
    { key: 'challenge_day', label: 'Challenge', align: 'left' },
    // Next to Challenge, so a challenger with reminders off is seen at once.
    { key: 'reminders', label: 'Reminders', align: 'left' },
    { key: 'practices', label: 'Practices' },
    { key: 'phrases', label: 'Phrases' },
    // `hint` is a grey line under the heading. On screen rather than a
    // tooltip, which a phone never shows.
    { key: 'checks_used', label: 'Checks', hint: 'scored this month' },
    { key: 'cost_eur', label: 'Cost', hint: 'API €, 30 days' },
    { key: 'days_active', label: 'Days', hint: 'practised, ever' },
    { key: 'last_active', label: 'Last practised' },
    { key: 'signed_up', label: 'Signed up' },
    { key: 'source', label: 'Came from', align: 'left' },
];

// Two filters that combine: WHO someone is, and WHAT they did in the chosen
// time window. "Paying" + "Signed up" + "7 days" is paying people who joined
// this week. Every count is worked out from the rows the table is about to
// show, so a chip and the list under it can never disagree.
const WHO = [
    { key: 'all', label: 'Everyone', test: () => true },
    { key: 'paid', label: 'Paying', test: u => u.paid_plan !== 'free' && !u.internal },
    { key: 'free', label: 'Free', test: u => u.plan === 'free' },
    { key: 'founding', label: 'Founding', test: u => u.founding_member },
    { key: 'challenge', label: 'In the challenge', test: u => !!u.challenge_day },
    // The Play Store app cannot sell anything, so a free Android user is
    // only reachable about upgrading by email. This is that list.
    { key: 'android', label: 'Android app', test: u => u.android },
    { key: 'reminders', label: 'Reminders on', test: u => u.reminders },
];
// The people worth a nudge, kept apart so they read as a to-do, not a segment.
const NUDGE = [
    { key: 'never', label: 'Never practised', test: u => !u.practices && !u.listens },
    { key: 'unfinished', label: 'Onboarding unfinished', test: u => !u.onboarded },
    { key: 'over_cap', label: 'Over the free cap', test: u => u.over_free_cap },
];
const WHO_ALL = [...WHO, ...NUDGE];

const WHEN = [
    { key: 'any', label: 'Any time', test: () => true },
    { key: 'active', label: 'Practised', test: (u, since) => (u.last_active || '') >= since },
    { key: 'signed', label: 'Signed up', test: (u, since) => (u.signed_up || '') >= since },
    { key: 'upgraded', label: 'Upgraded',
      test: (u, since) => u.paid_plan !== 'free' && !u.internal && (u.upgraded_at || '') >= since },
];

// One pill, used by every filter row, so the rows cannot drift apart.
const Chip = ({ active, onClick, count, children }) => (
    <button type="button" onClick={onClick}
            className={`rounded-full px-3 py-1 text-xs font-medium border ${
                active ? 'border-primary bg-primary/10 text-primary'
                    : 'border-stone-200 bg-white text-stone-600 hover:bg-stone-50'}`}>
        {children}
        {count != null && (
            <span className={`ml-1.5 ${active ? 'text-primary/70' : 'text-stone-400'}`}>{count}</span>
        )}
    </button>
);

// The challenge cards split into the November group (everyone who started on
// the cohort's start date) and everyone else, newest group first.
const challengeGroups = (participants, cohortStart) => {
    // Anyone whose day 1 falls in the cohort's month: people join up to 20 November.
    const nov = cohortStart ? participants.filter(p => inCohort(p, cohortStart)) : [];
    const rest = participants.filter(p => !nov.includes(p));
    return [
        nov.length ? { key: 'nov', label: 'November group', people: nov } : null,
        rest.length ? { key: 'rest', label: nov.length ? 'Earlier challenges' : 'Taking part', people: rest } : null,
    ].filter(Boolean);
};

// A finished challenge (past day 30, outside the November group) is archived:
// kept, but folded away and left out of the on-track counts.
const isArchived = (p, cohortStart) => !p.day && !inCohort(p, cohortStart);

// Started in the cohort's month ("2026-11"), on or after its first day.
function inCohort(p, cohortStart) {
    return !!(cohortStart && p.started && p.started >= cohortStart
        && p.started.slice(0, 7) === cohortStart.slice(0, 7));
}

// Archived runs grouped by the month they started ("September 2026"), newest
// month first.
const archiveMonths = (people) => {
    const groups = {};
    [...people].sort((a, b) => (b.started || '').localeCompare(a.started || '')).forEach(p => {
        const month = p.started
            ? new Date(`${p.started}T12:00:00`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
            : 'Unknown';
        (groups[month] = groups[month] || []).push(p);
    });
    return Object.entries(groups);
};

// November sign-ups from the /challenge page. Places taken out of 30, then
// everyone who started joining, with where they are.
const COHORT_STATUS = {
    waiting: ['Paid, waiting for the start', 'bg-emerald-50 text-emerald-700'],
    started: ['On Unlimited, started', 'bg-emerald-100 text-emerald-800'],
    phrases_pending: ['On Unlimited, day 1 when phrases are ready', 'bg-amber-50 text-amber-700'],
    payment_failed: ['First payment failed', 'bg-red-50 text-red-700'],
    cancelled: ['Cancelled', 'bg-stone-100 text-stone-500'],
    not_paid: ['Started joining, not paid', 'bg-amber-50 text-amber-700'],
    added: ['Already paying, added by you', 'bg-sky-50 text-sky-700'],
};

// Already-paying members can't join on the page (it would bill them twice), so
// they email and are added here.
const CohortBox = ({ cohort, onAdd }) => {
    const full = cohort.spots_taken >= cohort.spots_total;
    const [email, setEmail] = useState('');
    return (
        <div className="mb-4 rounded-2xl border border-stone-200 bg-white p-4">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="text-xs uppercase tracking-wide text-stone-500">November sign-ups</p>
                <p className="text-xs text-stone-500">Starts {cohort.starts}</p>
            </div>
            <p className={`mt-1 text-3xl font-bold ${full ? 'text-red-600' : 'text-foreground'}`}>
                {cohort.spots_taken} <span className="text-base font-medium text-stone-500">of {cohort.spots_total} places taken{full ? ', full' : ''}</span>
            </p>
            {cohort.people.length > 0 && (
                <ul className="mt-3 divide-y divide-stone-100">
                    {cohort.people.map(p => {
                        const [label, cls] = COHORT_STATUS[p.status] || [p.status, 'bg-stone-100 text-stone-600'];
                        return (
                            <li key={p.email} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                                <span className="min-w-0 break-all">{p.name || p.email} <span className="text-stone-400">{p.name ? p.email : ''}</span></span>
                                <span className="inline-flex items-center gap-2">
                                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>{label}</span>
                                    {p.status === 'added' && (
                                        <button type="button" onClick={() => onAdd(p.email, false)}
                                                className="text-xs text-stone-400 underline">remove</button>
                                    )}
                                </span>
                            </li>
                        );
                    })}
                </ul>
            )}
            <form
                className="mt-3 flex flex-wrap items-center gap-2 border-t border-stone-100 pt-3"
                onSubmit={e => { e.preventDefault(); if (email.trim()) { onAdd(email.trim(), true); setEmail(''); } }}
            >
                <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                       placeholder="Paying member's email"
                       className="min-w-0 flex-1 rounded-md border border-stone-300 px-2 py-1 text-sm" />
                <button type="submit" disabled={!email.trim()}
                        className="rounded-md bg-primary px-3 py-1 text-sm font-medium text-white disabled:opacity-40">
                    Add to November
                </button>
            </form>
        </div>
    );
};

// One participant in the 30-day challenge. The grid is the point: thirty cells,
// one a day, so a run of misses is visible without reading a single number.
const ChallengeDays = ({ days, length, target }) => (
    <div className="flex flex-wrap gap-1 mt-2">
        {Array.from({ length }, (_, i) => {
            const d = days[i];
            const tone = !d ? 'bg-stone-50 text-stone-300 border-stone-200'
                : d.hit ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                    : d.minutes > 0 ? 'bg-amber-100 text-amber-800 border-amber-300'
                        : 'bg-rose-50 text-rose-500 border-rose-200';
            const title = !d ? `Day ${i + 1}, still to come`
                : `Day ${d.day}, ${fmtDay(d.date)}\n${d.minutes} min`
                    + `${d.hit ? '' : ` (under ${target})`}`
                    + `${d.score != null ? `\nAverage ${d.score}%` : ''}`
                    + `${d.set_score != null ? `\nTailored set ${d.set_score}% over ${d.set_practices}` : ''}`;
            return (
                <span key={i} title={title}
                      className={`w-7 h-7 rounded-md border text-[10px] font-semibold
                                  flex items-center justify-center ${tone}
                                  ${d?.today ? 'ring-2 ring-primary ring-offset-1' : ''}`}>
                    {i + 1}
                </span>
            );
        })}
    </div>
);

// The challenge card's control: take someone out, or move their start day.
// Joining is on the challenge page, or the November box for paying members.
// The date box is there so a wrong start can be fixed without the database.
const ChallengeStart = ({ email, label, day, started, onSet }) => (
    // The day itself is in the header beside it, so only the controls here,
    // each with its words on screen.
    <span className="inline-flex items-center gap-3 flex-wrap text-stone-600">
        <label className="inline-flex items-center gap-1.5 cursor-pointer">
            <input
                type="checkbox"
                checked={!!day}
                onChange={e => onSet(email, e.target.checked)}
                aria-label={`30-day challenge: ${label}`}
                className="h-4 w-4 accent-orange-600 cursor-pointer"
            />
            In the challenge
        </label>
        {started && (
            <label className="inline-flex items-center gap-1.5">
                Started
                <input
                    type="date"
                    value={started}
                    onChange={e => e.target.value && onSet(email, true, e.target.value)}
                    aria-label={`Challenge start date: ${label}`}
                    className="rounded-md border border-stone-300 bg-white px-1.5 py-0.5 text-xs text-stone-600"
                />
            </label>
        )}
    </span>
);

// The 30 days as one line: every run of the tailored set, in order.
//
// Admin only, deliberately. The learner's own card shows the week blocks and
// their change since week one, which is encouragement. A line that can visibly
// dip is for the person running the challenge, not the person taking it.
//
// Hand-drawn SVG rather than a chart library: it is fifteen elements, and a
// dependency would cost more than it saves. A day with no run is a gap, not a
// zero, because a missed day is not a score of nothing.
const ChallengeChart = ({ days, length }) => {
    const runs = (days || []).filter(d => d.set_score != null);
    if (runs.length < 2) return null;

    const W = 320, H = 72, PAD = 5;
    const x = (day) => PAD + ((day - 1) / (length - 1)) * (W - PAD * 2);
    const y = (score) => H - PAD - (score / 100) * (H - PAD * 2);
    const first = runs[0], last = runs[runs.length - 1];
    const rising = last.set_score >= first.set_score;

    return (
        <div className="mt-3">
            <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-[72px]" role="img"
                 aria-label={`Tailored set, ${runs.length} runs, latest ${last.set_score}%`}>
                <line x1={PAD} x2={W - PAD} y1={y(first.set_score)} y2={y(first.set_score)}
                      stroke="currentColor" className="text-stone-300"
                      strokeDasharray="3 3" strokeWidth="1" />
                <polyline points={runs.map(d => `${x(d.day).toFixed(1)},${y(d.set_score).toFixed(1)}`).join(' ')}
                          fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                          stroke="currentColor"
                          className={rising ? 'text-emerald-500' : 'text-amber-500'} />
                {runs.map((d, i) => (
                    <circle key={d.date} cx={x(d.day)} cy={y(d.set_score)}
                            r={i === runs.length - 1 ? 3.5 : 2.5} fill="currentColor"
                            className={rising ? 'text-emerald-600' : 'text-amber-600'}>
                        <title>{`Day ${d.day}: ${d.set_score}% over ${d.set_practices} recordings`}</title>
                    </circle>
                ))}
            </svg>
            <p className="text-[10px] text-stone-400 mt-0.5">
                Day 1 to {length}, the tailored set only. Dotted line is their first run.
                Hover a point for that day.
            </p>
        </div>
    );
};

// Week by week on the tailored set. The set is meant to be run once a week, so
// a week with no run is the thing to spot, and it is amber rather than absent.
const ChallengeWeeks = ({ weeks }) => (
    <div className="grid grid-cols-5 gap-2 mt-3">
        {weeks.map(w => (
            <div key={w.week}
                 title={`${fmtDay(w.from)} to ${fmtDay(w.to)}`
                     + `\n${w.days_hit} of ${w.days} days done, ${w.minutes} min`
                     + `\n${w.set_runs
                         ? `First attempt at ${w.set_phrases} phrases, set run on ${w.set_runs} day${w.set_runs === 1 ? '' : 's'}`
                         : 'Tailored set not run'}`}
                 className={`rounded-lg border px-2 py-1.5 text-center ${
                     w.set_runs ? 'border-stone-200 bg-white'
                         : w.complete ? 'border-rose-200 bg-rose-50'
                             : 'border-amber-200 bg-amber-50'}`}>
                <p className="text-[10px] uppercase tracking-wide text-stone-500">
                    {w.final ? 'Final' : `Week ${w.week}`}
                </p>
                <p className="text-lg font-bold leading-tight text-foreground">
                    {w.set_score != null ? `${w.set_score}%` : '—'}
                </p>
                <p className="text-[10px] text-stone-500">
                    {w.set_runs
                        ? `${w.set_phrases} phrase${w.set_phrases === 1 ? '' : 's'}`
                        : w.complete ? 'set missed' : 'not yet'}
                </p>
            </div>
        ))}
    </div>
);

// Signed change in points, "+4" or "-2", or nothing when there is nothing to compare.
const signed = (n) => (n == null ? null : `${n >= 0 ? '+' : ''}${n}`);
const pct = (v) => (v == null ? '—' : `${v}%`);

// Reminders on or off, the same pill on the list and on the detail page.
const ReminderPill = ({ on, long = false }) => (on == null ? null : (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${
        on ? 'bg-emerald-50 text-emerald-700' : 'bg-stone-100 text-stone-500'}`}>
        {long ? 'Reminders ' : ''}{on ? (long ? 'on' : 'On') : (long ? 'off' : 'Off')}
    </span>
));

// Who needs you first: set overdue, then the most days missed.
const byConcern = (a, b) => (Number(!!b.set_overdue) - Number(!!a.set_overdue))
    || ((b.days_missed || 0) - (a.days_missed || 0))
    || (a.name || a.email || '').localeCompare(b.name || b.email || '');

// A person's sounds worth reading: in at least three recordings, weakest first.
const weakSounds = (p, limit) => (p.sounds || []).filter(s => s.recs >= 3).slice(0, limit);

const mean = (xs) => {
    const v = xs.filter(x => x != null);
    return v.length ? Math.round(v.reduce((a, b) => a + b, 0) / v.length * 10) / 10 : null;
};

// The whole group in one place: how scores have moved, how many days got done,
// and the sounds the group finds hardest, added up from each person's raw
// totals so it agrees with their own pages.
const ChallengeGroup = ({ people }) => {
    if (!people.length) return null;
    const hit = people.reduce((n, p) => n + (p.days_hit || 0), 0);
    const elapsed = people.reduce((n, p) => n + (p.days_elapsed || 0), 0);
    const dayOne = mean(people.map(p => p.overall?.day_one));
    const latest = mean(people.map(p => p.overall?.latest_day));
    const paired = people.map(p => p.paired?.change).filter(c => c != null);
    const pairedAvg = mean(paired);

    const tot = {};
    people.forEach(p => {
        const weakest = new Set(weakSounds(p, 3).map(s => s.sound));
        (p.sounds || []).forEach(s => {
            const t = tot[s.sound] = tot[s.sound] || { label: s.label, total: 0, n: 0, recs: 0, people: 0, weak: 0 };
            t.total += s.total;
            t.n += s.n;
            t.recs += s.recs;
            t.people += 1;
            if (weakest.has(s.sound)) t.weak += 1;
        });
    });
    const hardest = Object.values(tot)
        .filter(t => t.recs >= 5)
        .map(t => ({ ...t, avg: Math.round(t.total / t.n) }))
        .sort((a, b) => a.avg - b.avg)
        .slice(0, 6);

    return (
        <Section title={`The group so far · ${num(people.length)}`} id="group">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Stat label="Overall, day 1" value={pct(dayOne)} sub="average of everyone" />
                <Stat label="Overall, latest" value={pct(latest)}
                      tone={dayOne != null && latest != null ? (latest >= dayOne ? 'good' : 'warn') : 'default'}
                      sub={dayOne != null && latest != null ? `${signed(Math.round((latest - dayOne) * 10) / 10)} points` : 'needs a second day'} />
                <Stat label="Like for like" value={pairedAvg == null ? '—' : signed(pairedAvg)}
                      tone={pairedAvg == null ? 'default' : pairedAvg > 0 ? 'good' : pairedAvg < 0 ? 'warn' : 'default'}
                      sub={paired.length ? `points, average of ${num(paired.length)} ${paired.length === 1 ? 'person' : 'people'}` : 'needs both halves'} />
                <Stat label="Days done" value={elapsed ? `${Math.round(hit / elapsed * 100)}%` : '—'}
                      sub={`${num(hit)} of ${num(elapsed)} days`} />
            </div>
            <p className="text-xs uppercase tracking-wide text-stone-500 mt-4 mb-1">Hardest sounds for the group</p>
            {hardest.length ? (
                <List>
                    {hardest.map(t => (
                        <Row key={t.label} left={t.label}
                             sub={`weakest 3 for ${num(t.weak)} of ${num(people.length)} · ${num(t.recs)} recordings`}
                             right={`${t.avg}%`} />
                    ))}
                </List>
            ) : <p className="text-sm text-stone-500">Not enough scored recordings yet.</p>}
        </Section>
    );
};

// Everyone in one challenge group, two lines each so it fits a phone without
// scrolling sideways. Tap a name for the detail.
const ChallengeTable = ({ people, length, target, reminders, onPick }) => (
    <List>
        {[...people].sort(byConcern).map(p => (
            <button key={`${p.email}-${p.started}`} type="button" onClick={() => onPick(p)}
                    className="w-full text-left px-4 py-2.5 hover:bg-stone-50 block">
                <span className="flex items-baseline justify-between gap-3">
                    <span className="min-w-0 truncate font-semibold text-primary underline">{p.name || p.email}</span>
                    <span className="shrink-0 inline-flex items-center gap-2 text-xs">
                        <ReminderPill on={reminders[(p.email || '').toLowerCase()]} long />
                        {p.finished
                            ? <span className="text-stone-400">Finished</span>
                            : <span className="font-semibold text-orange-700">Day {p.day} of {length}</span>}
                    </span>
                </span>
                <span className="mt-0.5 block text-xs text-stone-500">
                    <span className={`font-semibold ${
                        p.on_track ? 'text-emerald-700' : p.days_missed > 3 ? 'text-rose-600' : 'text-amber-700'}`}>
                        {num(p.days_hit)}/{num(p.days_elapsed)} days
                    </span>
                    {` of ${target}+ min · overall ${pct(p.overall?.day_one)} → ${pct(p.overall?.latest_day)}`}
                    {p.overall?.day_change != null && (
                        <span className={`font-semibold ${p.overall.day_change >= 0 ? 'text-emerald-700' : 'text-amber-700'}`}>
                            {' '}{signed(p.overall.day_change)}
                        </span>
                    )}
                    {` · set ${pct(p.baseline?.avg)} → ${pct(p.latest?.avg)}`}
                    {p.change != null && (
                        <span className={`font-semibold ${p.change >= 0 ? 'text-emerald-700' : 'text-amber-700'}`}>
                            {' '}{signed(p.change)}
                        </span>
                    )}
                    {' · '}
                    <span className={p.set_overdue ? 'font-semibold text-rose-600' : undefined}>
                        {p.last_set_run ? `set run ${fmtDay(p.last_set_run)}` : 'set never run'}
                        {p.set_overdue && ', chase'}
                    </span>
                </span>
            </button>
        ))}
    </List>
);

// One challenger, everything worth knowing on one screen. `archived` is a
// finished run kept for reading: no tick or date, which would act on the
// account's current challenge, not this one.
const ChallengeDetail = ({ p, length, target, onSet, onBack, archived = false, reminders }) => {
    // The monthly Speechace cap is waived while someone is taking part, so it
    // only matters once they have finished.
    const capMatters = p.finished && p.checks_left != null && p.checks_left < 60;
    const facts = [
        ['Practice so far', `${num(p.minutes_total)} min, ${num(p.practices_total)} practices`],
        ['Tailored set', p.collection
            ? `${p.collection}, ${num(p.collection_phrases)} phrases, run ${num(p.set_runs)}`
              + ` ${p.set_runs === 1 ? 'time' : 'times'}`
            : 'none found: name their collection with "challenge" in it'],
        p.focus_sounds?.length > 0 && ['Sounds the set was built for',
            p.focus_sounds.map(s => `${s.label}${s.score != null ? ` ${num(s.score)}` : ''}`).join(', ')],
        capMatters && ['Accuracy checks', `${num(p.checks_used)} of ${num(p.checks_cap)}, ${num(p.checks_left)} left`],
    ].filter(Boolean);

    return (
        <div className="mt-5">
            <button type="button" onClick={onBack} className="text-sm text-primary underline">
                ← All challengers
            </button>

            <div className="mt-3 rounded-xl border border-stone-200 bg-white p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                        <p className="font-semibold text-foreground">
                            {p.name || p.email}{' '}
                            <span className="inline-flex align-middle ml-1 gap-1">
                                <PlanBadge plan={p.plan} />
                                <ReminderPill on={reminders} long />
                            </span>
                        </p>
                        <p className="text-xs text-stone-500 break-all">{p.email}</p>
                        <span className="inline-flex mt-1.5 text-xs">
                            {archived
                                ? <span className="text-stone-400">Started {fmtDay(p.started)}</span>
                                : <ChallengeStart email={p.email} label={p.name || p.email}
                                                  day={p.day} started={p.started} onSet={onSet} />}
                        </span>
                    </div>
                    <span className={`text-sm font-semibold shrink-0 ${
                        p.finished ? 'text-stone-400' : 'text-orange-700'}`}>
                        {p.finished ? 'Finished' : `Day ${p.day} of ${length}`}
                    </span>
                </div>

                {p.set_overdue && (
                    <p className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-medium text-rose-700">
                        {p.days_since_set_run == null
                            ? 'Tailored set not run once yet. Worth a nudge.'
                            : `Tailored set not run for ${num(p.days_since_set_run)} days. Worth a nudge.`}
                    </p>
                )}

                <p className="text-xs uppercase tracking-wide text-stone-500 mt-4 mb-1">Everything they practise</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <Stat label="Overall, day 1" value={pct(p.overall?.day_one)}
                          sub={p.overall?.day_one_date ? fmtDay(p.overall.day_one_date) : 'nothing scored yet'} />
                    <Stat label="Overall, latest" value={pct(p.overall?.latest_day)}
                          tone={p.overall?.day_change == null ? 'default' : p.overall.day_change >= 0 ? 'good' : 'warn'}
                          sub={p.overall?.day_change == null ? 'needs a second day' : `${signed(p.overall.day_change)} points`} />
                    <Stat label="Like for like" value={p.paired?.change == null ? '—' : signed(p.paired.change)}
                          tone={p.paired?.change == null ? 'default' : p.paired.change > 0 ? 'good' : p.paired.change < 0 ? 'warn' : 'default'}
                          sub={p.paired?.change == null ? 'needs both halves' : `points, ${num(p.paired.phrases)} phrases`} />
                    <Stat label="Phrases mastered" value={num(p.mastered?.now)}
                          tone={p.mastered?.change > 0 ? 'good' : 'default'}
                          sub={p.mastered ? `${num(p.mastered.at_start)} at the start` : null} />
                </div>

                <p className="text-xs uppercase tracking-wide text-stone-500 mt-4 mb-1">Tailored set</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <Stat label="Set, day 1" value={pct(p.baseline?.avg)}
                          sub={p.baseline ? fmtDay(p.baseline.date) : 'not run yet'} />
                    <Stat label="Set, now" value={pct(p.latest?.avg)}
                          tone={p.change == null ? 'default' : p.change >= 0 ? 'good' : 'warn'}
                          sub={p.change == null ? 'needs a second run' : `${signed(p.change)} points`} />
                    <Stat label="Set, like for like"
                          value={p.paired_set?.change == null ? '—' : signed(p.paired_set.change)}
                          tone={p.paired_set?.change == null ? 'default' : p.paired_set.change > 0 ? 'good' : p.paired_set.change < 0 ? 'warn' : 'default'}
                          sub={p.paired_set?.change == null ? 'needs runs in both halves' : `points, ${num(p.paired_set.phrases)} phrases`} />
                    <Stat label={`Days of ${target}+ min`} value={`${num(p.days_hit)}/${num(p.days_elapsed)}`}
                          tone={p.on_track ? 'good' : p.days_missed > 3 ? 'bad' : 'warn'}
                          sub={p.on_track ? 'not missed one' : `${num(p.days_missed)} missed`} />
                </div>

                <ChallengeDays days={p.days} length={length} target={target} />
                <ChallengeWeeks weeks={p.weeks} />
                <ChallengeChart days={p.days} length={length} />

                <p className="text-xs uppercase tracking-wide text-stone-500 mt-4 mb-1">Weakest sounds in the challenge</p>
                {weakSounds(p, 5).length ? (
                    <List>
                        {weakSounds(p, 5).map(snd => (
                            <Row key={snd.sound} left={snd.label}
                                 sub={`${num(snd.recs)} recordings`
                                     + (snd.early != null && snd.late != null ? ` · ${snd.early}% first half, ${snd.late}% second` : '')}
                                 right={`${snd.avg}%`} />
                        ))}
                    </List>
                ) : <p className="text-sm text-stone-500">Not enough scored recordings yet.</p>}

                <div className="mt-4">
                    <List>
                        {facts.map(([label, value]) => (
                            <Row key={label} left={label} sub={value} />
                        ))}
                    </List>
                </div>
            </div>
        </div>
    );
};

// A paid plan given for free until a date.
//
// It ends by itself on the morning after the last day, because the server works
// the plan out on every read rather than switching it with a job. Nothing to
// diary, and the row shows what somebody is actually getting today rather than
// what they pay for.
const CompCell = ({ user, onSet }) => {
    const [open, setOpen] = useState(false);
    const [plan, setPlan] = useState('pro');
    const [until, setUntil] = useState('');
    const [why, setWhy] = useState('');

    if (user.comp_plan && !open) {
        return (
            // Stacked, plan over date over "end", to keep the column narrow.
            <span className="inline-flex flex-col items-start leading-tight">
                {/* The reason is a hover, not a column. The dotted underline
                    is the only hint that there is one to read. */}
                <span className={`font-semibold text-emerald-700${user.comp_reason
                          ? ' underline decoration-dotted decoration-emerald-400 underline-offset-2 cursor-help' : ''}`}
                      title={user.comp_reason || undefined}>
                    {user.comp_plan}
                </span>
                <span className="text-stone-500">to {fmtDay(user.comp_until)}</span>
                <button type="button" onClick={() => onSet(user.email, null)}
                        className="text-primary underline">end</button>
            </span>
        );
    }
    if (!open) {
        return (
            <button type="button" onClick={() => setOpen(true)}
                    className="text-primary underline whitespace-nowrap">give free plan</button>
        );
    }
    return (
        // Stacked while editing too, or opening it blows the column wide.
        <span className="inline-flex flex-col items-start gap-1">
            <select value={plan} onChange={e => setPlan(e.target.value)}
                    className="rounded-md border border-stone-300 px-1 py-0.5 text-xs">
                <option value="pro">Pro</option>
                <option value="unlimited">Unlimited</option>
            </select>
            <input type="date" value={until} onChange={e => setUntil(e.target.value)}
                   aria-label="Free until, last day included"
                   className="rounded-md border border-stone-300 px-1 py-0.5 text-xs" />
            <input value={why} onChange={e => setWhy(e.target.value)} placeholder="why"
                   className="rounded-md border border-stone-300 px-1.5 py-0.5 text-xs w-24" />
            <span className="inline-flex gap-2">
                <button type="button" disabled={!until}
                        onClick={() => { onSet(user.email, plan, until, why); setOpen(false); }}
                        className="text-primary underline disabled:opacity-40">save</button>
                <button type="button" onClick={() => setOpen(false)}
                        className="text-stone-400 underline">cancel</button>
            </span>
        </span>
    );
};

const UsersTable = ({ users, who, setWho, when, setWhen, period, setPeriod, onSetComp, novemberEmails = new Set() }) => {
    const [query, setQuery] = useState('');
    const [sort, setSort] = useState({ key: 'practices', dir: 'desc' });

    const q = query.trim().toLowerCase();
    const since = periodStart(period);
    const whoF = WHO_ALL.find(f => f.key === who) || WHO[0];
    const whenF = WHEN.find(f => f.key === when) || WHEN[0];
    const rows = users
        .filter(whoF.test)
        .filter(u => whenF.test(u, since))
        .filter(u => !q || `${u.name} ${u.email}`.toLowerCase().includes(q))
        .sort((a, b) => {
            const [x, y] = [a[sort.key], b[sort.key]];
            // Blanks last whichever way it is sorted: an empty date is not
            // "earliest", it is "never", and it belongs at the bottom.
            if (x == null || x === '') return 1;
            if (y == null || y === '') return -1;
            const cmp = typeof x === 'number' && typeof y === 'number'
                ? x - y : String(x).localeCompare(String(y));
            return sort.dir === 'asc' ? cmp : -cmp;
        });

    const toggleSort = (key) => setSort(s => ({
        key, dir: s.key === key && s.dir === 'desc' ? 'asc' : 'desc',
    }));

    const cell = (u, key) => {
        if (key === 'first_name') return (
            <span className="block max-w-[11rem]">
                <span className="block truncate font-semibold text-foreground">{u.first_name || '—'}</span>
                <span className="block truncate text-stone-500">{u.email}</span>
            </span>
        );
        if (key === 'plan') return (
            <span className="inline-flex flex-col items-start gap-1">
                <PlanBadge plan={u.plan} />
                {u.founding_member && <FoundingBadge />}
                {u.android && (
                    <span className="rounded-full bg-sky-50 text-sky-700 ring-1 ring-inset ring-sky-200 px-2 py-0.5 text-[10px] font-semibold">
                        Android
                    </span>
                )}
                <CompCell user={u} onSet={onSetComp} />
            </span>
        );
        // Read only. People join on the challenge page or from the November
        // box; corrections are on their challenge card.
        // A November place outranks "done": someone back for a second
        // challenge reads as waiting for November, not finished.
        if (key === 'challenge_day') return u.challenge_day
            ? <span className="text-orange-700 font-semibold">Day {u.challenge_day}</span>
            : novemberEmails.has((u.email || '').toLowerCase())
                ? <span className="text-sky-700 font-semibold">November</span>
                : u.challenge_started ? <span className="text-stone-400">done</span> : '—';
        if (key === 'cost_eur') return u.cost_eur ? eur(u.cost_eur) : '—';
        if (key === 'reminders') return u.reminders
            ? <span className="text-emerald-700 font-semibold">On</span>
            : <span className="text-stone-400">Off</span>;
        if (key === 'last_active') return u.last_active ? fmtDay(u.last_active) : '—';
        if (key === 'signed_up') return fmtDay(u.signed_up);
        if (key === 'source') {
            if (!u.source) return <span className="text-stone-400" title="Signed up before this was recorded">—</span>;
            const d = u.source_detail || {};
            // Everything we know, on hover: the bare label alone cannot tell a
            // tagged campaign link apart from a plain click on the same site.
            const detail = [
                d.utm_campaign && `campaign: ${d.utm_campaign}`,
                d.utm_medium && `medium: ${d.utm_medium}`,
                d.referrer && `referrer: ${d.referrer}`,
                d.landing_path && `landed on: ${d.landing_path}`,
            ].filter(Boolean).join('\n');
            return <span className="block max-w-[7rem] truncate" title={detail || undefined}>{u.source}</span>;
        }
        return num(u[key]);
    };

    return (
        <div>
            <input value={query} onChange={e => setQuery(e.target.value)}
                   placeholder="Search name or email"
                   className="w-full rounded-xl border border-stone-200 px-3 py-1.5 text-sm mb-3" />

            {/* Who: counts are for everyone, whatever the time filter says. */}
            <div className="rounded-xl border border-stone-200 bg-white p-3 space-y-3 mb-3">
                <div>
                    <p className="text-xs uppercase tracking-wide text-stone-500 mb-1.5">Who</p>
                    <div className="flex flex-wrap gap-2">
                        {WHO.map(f => (
                            <Chip key={f.key} active={who === f.key} onClick={() => setWho(f.key)}
                                  count={num(users.filter(f.test).length)}>{f.label}</Chip>
                        ))}
                    </div>
                </div>
                <div>
                    <p className="text-xs uppercase tracking-wide text-stone-500 mb-1.5">Needs a nudge</p>
                    <div className="flex flex-wrap gap-2">
                        {NUDGE.map(f => (
                            <Chip key={f.key} active={who === f.key} onClick={() => setWho(f.key)}
                                  count={num(users.filter(f.test).length)}>{f.label}</Chip>
                        ))}
                    </div>
                </div>
                {/* What happened, and when. The counts here follow the Who
                    choice above, so they add up to what the table shows. */}
                <div>
                    <p className="text-xs uppercase tracking-wide text-stone-500 mb-1.5">What they did</p>
                    <div className="flex flex-wrap items-center gap-2">
                        {WHEN.map(f => (
                            <Chip key={f.key} active={when === f.key} onClick={() => setWhen(f.key)}
                                  count={f.key === 'any' ? null
                                      : num(users.filter(whoF.test).filter(u => f.test(u, since)).length)}>
                                {f.label}
                            </Chip>
                        ))}
                        {when !== 'any' && (
                            <Segmented options={PERIODS} value={period} onChange={setPeriod} />
                        )}
                    </div>
                </div>
            </div>

            <p className="text-xs text-stone-500 mb-1">
                <span className="font-semibold text-stone-700">{num(rows.length)} {rows.length === 1 ? 'person' : 'people'}</span>
                {': '}{whoF.label.toLowerCase()}
                {when !== 'any' && `, ${whenF.label.toLowerCase()} ${period === 'today' ? 'today' : `in the last ${periodLabel(period)}`}`}
                {(who !== 'all' || when !== 'any') && (
                    <button type="button" onClick={() => { setWho('all'); setWhen('any'); }}
                            className="ml-2 text-primary underline">clear filters</button>
                )}
            </p>

            {/* The table scrolls inside its own box; the page never scrolls sideways. */}
            <div className="overflow-x-auto rounded-xl border border-stone-200 bg-white">
                <table className="w-full text-xs">
                    <thead>
                        <tr className="border-b border-stone-100">
                            {COLUMNS.map(c => (
                                <th key={c.key}
                                    onClick={() => toggleSort(c.key)}
                                    className={`px-2 py-2 align-bottom font-semibold leading-tight text-stone-500 cursor-pointer select-none hover:text-foreground ${
                                        c.align === 'left' ? 'text-left' : 'text-right'}`}>
                                    {c.label}
                                    {sort.key === c.key && <span className="ml-1">{sort.dir === 'desc' ? '↓' : '↑'}</span>}
                                    {c.hint && <span className="block max-w-[5rem] ml-auto text-[10px] font-normal text-stone-400">{c.hint}</span>}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(u => (
                            <tr key={u.email} className="border-b border-stone-50 last:border-0 hover:bg-stone-50">
                                {COLUMNS.map(c => (
                                    <td key={c.key}
                                        className={`px-2 py-2 whitespace-nowrap ${
                                            c.align === 'left' ? 'text-left' : 'text-right text-stone-600'}`}
                                        title={c.key === 'first_name' ? u.email : undefined}>
                                        {cell(u, c.key)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                        {rows.length === 0 && (
                            <tr><td colSpan={COLUMNS.length} className="px-3 py-4 text-stone-500">Nobody matches that.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// "x of y used" against a free-tier ceiling, so a bill never arrives as a surprise.
const Quota = ({ label, used, limit, unit = '', note }) => {
    const pct = limit ? Math.min(100, (used / limit) * 100) : 0;
    return (
        <div className="rounded-xl border border-stone-200 bg-white px-4 py-3">
            <div className="flex justify-between text-sm">
                <span className="text-foreground">{label}</span>
                <span className="text-stone-500">
                    {num(used)}{unit} of {num(limit)}{unit}
                </span>
            </div>
            <div className="h-1.5 bg-stone-100 rounded-full mt-2 overflow-hidden">
                <div className={`h-full rounded-full ${pct > 90 ? 'bg-rose-500' : pct > 70 ? 'bg-amber-500' : 'bg-primary/70'}`}
                     style={{ width: `${pct}%` }} />
            </div>
            {note && <p className="text-xs text-stone-500 mt-1.5">{note}</p>}
        </div>
    );
};

// Everything is stored in UTC and read in France, so every date on this page
// is converted, and written the UK way round.
const PARIS = 'Europe/Paris';
const fmtDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d)) return String(iso);
    return d.toLocaleString('en-GB', {
        timeZone: PARIS, day: '2-digit', month: '2-digit', year: '2-digit',
        hour: '2-digit', minute: '2-digit',
    });
};
// dd/mm/yy, same as the rest of the app; the dash is what admin tables show
// for a value that isn't there.
const fmtDay = (iso) => formatDate(iso) || '—';
const money = (amount, currency) => {
    if (amount === null || amount === undefined) return '—';
    try {
        return new Intl.NumberFormat('en-GB', {
            style: 'currency', currency: (currency || 'eur').toUpperCase(),
        }).format(amount);
    } catch {
        return `${amount} ${(currency || '').toUpperCase()}`;
    }
};
const num = (n) => (n === null || n === undefined ? '—' : Number(n).toLocaleString('en-GB'));
// Everything is shown in euros, converted on the server. Spend is small enough
// that rounding to cents would read as a flat zero all month, so keep three
// places under a euro.
const eur = (n) => {
    const v = Number(n || 0);
    if (v > 0 && v < 1) return `€${v.toFixed(3)}`;
    return money(v, 'eur');
};

const SERVICE_LABELS = {
    gemini: 'Gemini (AI text)',
    elevenlabs: 'ElevenLabs (voices)',
    speechace: 'SpeechAce (scoring)',
    resend: 'Resend (email)',
};
const SERVICE_UNITS = {
    gemini: 'tokens',
    elevenlabs: 'characters',
    speechace: '15-second requests',
    resend: 'emails',
};

export default function AdminStats() {
    const [data, setData] = useState(null);
    const [ops, setOps] = useState(null);
    const [error, setError] = useState(null);
    // The dashboard is the admin home now, so it carries the login rather than
    // sending people to a separate page to get a token first.
    const [token, setToken] = useState(sessionStorage.getItem('mimique_admin_token'));
    const base = process.env.REACT_APP_BACKEND_URL || '';

    const get = (path) =>
        fetch(`${base}${path}`, { headers: { Authorization: `Bearer ${token}` } })
            .then(r => {
                if (r.status === 401) { sessionStorage.removeItem('mimique_admin_token'); setToken(null); throw new Error('That password is no longer valid. Log in again.'); }
                if (!r.ok) throw new Error(`Server returned ${r.status}`);
                return r.json();
            });

    const [people, setPeople] = useState(null);
    // The top row's numbers, for all three windows in one go, so the switch
    // never waits on the server.
    const [periods, setPeriods] = useState(null);
    const [period, setPeriod] = useState('today');
    // The tab lives in the address, so a refresh or a bookmark lands back on it.
    const [params, setParams] = useSearchParams();
    const tab = TABS.some(([k]) => k === params.get('tab')) ? params.get('tab') : 'overview';
    const setTab = (key) => {
        setParams(key === 'overview' ? {} : { tab: key });
        window.scrollTo({ top: 0 });
    };

    // Read separately from the users table: the table is lifetime totals, and
    // the challenge is only answerable day by day.
    const [challenge, setChallenge_] = useState(null);
    const loadChallenge = () => get('/api/admin/challenge')
        .then(setChallenge_)
        .catch(e => toast.error(`Challenge: ${e.message}`));
    // The November sign-ups from the /challenge page: places taken, and who
    // started joining without paying.
    const [cohort, setCohort] = useState(null);
    const [showArchive, setShowArchive] = useState(false);
    // The challenger whose page is open, or null for the list. Email and start
    // together, since one person can have an archived run and a current one.
    const [pickedChallenger, setPickedChallenger] = useState(null);
    const loadCohort = () => get('/api/admin/challenge/cohort')
        .then(setCohort)
        .catch(e => toast.error(`November sign-ups: ${e.message}`));

    const [who, setWho] = useState('all');
    const [when, setWhen] = useState('any');

    // Everyone, once. Who was active when is worked out in the browser from
    // each person's last practice day, so the filters never go back to the
    // server. 30 days is only what the Cost column covers.
    const loadPeople = () => {
        get('/api/admin/users?window=30')
            .then(setPeople)
            .catch(e => toast.error(`Users: ${e.message}`));
    };

    // Reading every subscriber takes a few MailerLite calls, so it waits to be
    // asked. Read-only: removing anyone is done in MailerLite.
    const [mlDetail, setMlDetail] = useState(null);
    const [mlChecking, setMlChecking] = useState(false);
    // Stripe is the authority on who is paying; our plan field is only a copy
    // of it. This says whether the two still agree.
    const [stripeCheck, setStripeCheck] = useState(null);
    const [stripeChecking, setStripeChecking] = useState(false);
    const checkStripe = () => {
        setStripeChecking(true);
        get('/api/admin/stripe-check')
            .then(setStripeCheck)
            .catch(e => toast.error(`Stripe: ${e.message}`))
            .finally(() => setStripeChecking(false));
    };

    const checkMailerlite = () => {
        setMlChecking(true);
        get('/api/admin/mailerlite')
            .then(setMlDetail)
            .catch(e => toast.error(`MailerLite: ${e.message}`))
            .finally(() => setMlChecking(false));
    };

    const load = () => {
        get('/api/admin/stats').then(setData).catch(e => { setError(e.message); toast.error(e.message); });
        get('/api/admin/ops').then(setOps).catch(e => toast.error(`Ops: ${e.message}`));
        get('/api/admin/periods').then(setPeriods).catch(e => toast.error(`Top row: ${e.message}`));
        loadPeople();
        loadChallenge();
        loadCohort();
        checkInbox();
    };

    useEffect(() => { if (token) load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

    // Two IMAP logins take a few seconds, so this is a button rather than part
    // of the page load. It only ever reads: it cannot mark mail read or send.
    const [inbox, setInbox] = useState(null);
    const [checking, setChecking] = useState(false);
    // Hides everything of that kind up to now. If it happens again it comes
    // back, so a fix that did not work cannot stay hidden.
    const resolveError = (name, undo = false) => {
        fetch(`${base}/api/admin/errors/${undo ? 'unresolve' : 'resolve'}`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ name }),
        })
            .then(r => { if (!r.ok) throw new Error(`Server returned ${r.status}`); })
            .then(() => { toast.success(undo ? `${name} is back` : `${name} marked as sorted`); load(); })
            .catch(e => toast.error(e.message));
    };

    // Give somebody a paid plan for free until a date, or end it early.
    // Passing no plan clears it.
    const setComp = (email, plan, until, reason) => {
        fetch(`${base}/api/admin/users/comp`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, plan: plan || null, until: until || null, reason: reason || '' }),
        })
            .then(async r => {
                if (!r.ok) throw new Error(
                    (await r.json().catch(() => null))?.detail || `Server returned ${r.status}`);
            })
            .then(() => {
                toast.success(plan ? `${email} has ${plan} free until ${until}`
                    : `${email} back on their own plan`);
                load();
            })
            .catch(e => toast.error(e.message));
    };

    // Ticking records today in Paris as day 1, so the badge clears itself on
    // day 31. Re-ticking someone already taking part does not restart them.
    // Passing a date sets the start outright, which is how a tick on the wrong
    // evening gets corrected.
    const setChallenge = (email, value, started) => {
        fetch(`${base}/api/admin/users/challenge`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, in_challenge: value, started: started || null }),
        })
            .then(async r => {
                // The server explains a bad date in words, so show that rather
                // than a status code nobody can act on.
                if (!r.ok) throw new Error(
                    (await r.json().catch(() => null))?.detail || `Server returned ${r.status}`);
            })
            .then(() => {
                toast.success(!value ? `${email} taken out of the challenge`
                    : started ? `${email} now starts on ${fmtDay(started)}`
                        : `${email} started the challenge today`);
                load();
            })
            .catch(e => toast.error(e.message));
    };

    const addToCohort = (email, add) => {
        fetch(`${base}/api/admin/challenge/cohort/add`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, add }),
        })
            .then(async r => {
                const body = await r.json().catch(() => null);
                if (!r.ok) throw new Error(body?.detail || `Server returned ${r.status}`);
                return body;
            })
            .then(body => {
                toast.success(!add ? `${email} taken out of November`
                    : body?.emailed ? `${email} added to November, welcome email sent`
                        : `${email} added to November (the welcome email didn't send)`);
                loadCohort();
            })
            .catch(e => toast.error(e.message));
    };

    const checkInbox = () => {
        setChecking(true);
        get('/api/admin/inbox')
            .then(setInbox)
            .catch(e => toast.error(`Inbox: ${e.message}`))
            .finally(() => setChecking(false));
    };

    // Show me these people: set both filters and go to the Users tab. The time
    // window carries over from wherever you tapped.
    const showUsers = (whoKey, whenKey = 'any') => {
        setWho(whoKey);
        setWhen(whenKey);
        setTab('users');
    };

    // Go to the tab a section lives on, then to the section itself.
    const reveal = (key) => {
        setTab(SECTION_TAB[key] || 'overview');
        setTimeout(() => document.getElementById(key)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
    };

    if (!token) return <AdminLogin onAuthed={setToken} />;
    if (error) {
        return (
            <div className="min-h-screen bg-background px-4 py-12 max-w-3xl mx-auto">
                <AdminNav />
                <p className="text-sm text-rose-600">{error}</p>
            </div>
        );
    }
    if (!data) {
        return (
            <div className="min-h-screen bg-background px-4 py-10 max-w-3xl mx-auto">
                <AdminNav />
                <p className="text-sm text-stone-500">Loading…</p>
            </div>
        );
    }

    const { users, signups, subscribers, over_cap: overCap, usage, reminders, mailerlite: ml, cancellations: cancels } = data;

    // Only counts mailboxes we can actually read, so "0 waiting" never means
    // "nothing is connected".
    const liveMailboxes = (inbox?.mailboxes || []).filter(m => m.configured && !m.error);
    const inboxWaiting = liveMailboxes.length
        ? liveMailboxes.reduce((t, m) => t + (m.unread || 0), 0)
        : null;

    const errors = ops?.errors;
    const emails = ops?.emails;
    const spend = ops?.spend;
    const funnel = ops?.funnel;
    const el = spend?.elevenlabs;
    const revenue = ops?.revenue;

    // The grant is huge, so "0.09% used" says nothing useful. What matters is
    // how long it lasts at the rate we are burning it, and when it expires.
    const elChars = spend?.services?.find(s => s.service === 'elevenlabs')?.units || 0;
    const elPerDay = elChars / Math.max(1, spend?.daily?.length || 1);
    const elLeft = el?.limit != null && el?.used != null ? el.limit - el.used : null;
    const elRunway = elPerDay > 0 && elLeft != null ? elLeft / elPerDay : null;
    const runwayText = elRunway === null ? null
        : elRunway > 3650 ? 'far longer than the grant itself lasts'
            : elRunway > 730 ? `${Math.round(elRunway / 365)} years`
                : elRunway > 60 ? `${Math.round(elRunway / 30)} months`
                    : `${Math.round(elRunway)} days`;
    const peakSpend = Math.max(0.000001, ...(spend?.daily || []).map(d => d.cost_eur));

    const challengers = challenge?.participants || [];
    const activeChallengers = challengers.filter(p => !isArchived(p, cohort?.starts));
    const archivedChallengers = challengers.filter(p => isArchived(p, cohort?.starts));
    // Reminder on or off per email, for the challenge cards. Empty until the
    // users list has loaded, so a card shows nothing rather than a wrong "off".
    const remindersByEmail = Object.fromEntries((people?.users || [])
        .map(u => [(u.email || '').toLowerCase(), !!u.reminders]));
    // Everyone holding a November place, for the users table.
    const novemberEmails = new Set((cohort?.people || [])
        .filter(p => ['waiting', 'added', 'payment_failed', 'started'].includes(p.status))
        .map(p => (p.email || '').toLowerCase()));

    // The top row. Each box reads the same window from /api/admin/periods,
    // and taps through to the tab, or the people, behind its number.
    const PERIOD_BOXES = [
        { key: 'active', label: 'Active users', go: () => showUsers('all', 'active') },
        { key: 'signups', label: 'New sign-ups', tone: 'good', go: () => showUsers('all', 'signed') },
        { key: 'upgrades', label: 'Upgrades', tone: 'good', go: () => showUsers('all', 'upgraded') },
        { key: 'cancellations', label: 'Cancellations', tone: 'warn', go: () => reveal('cancellations') },
        { key: 'revenue', label: 'Earned, after fees', tone: 'good', go: () => reveal('money'),
          fmt: r => (r.configured === false ? '—' : money(r.net, r.currency)), amount: r => r.net },
        { key: 'spend_eur', label: 'API spend', fmt: eur, go: () => reveal('costs') },
        { key: 'errors', label: 'Errors', tone: 'bad', go: () => reveal('errors') },
        { key: 'emails_failed', label: 'Emails failed', tone: 'bad', go: () => reveal('emails') },
    ];
    const onTrack = activeChallengers.filter(p => p.on_track).length;
    const chasing = activeChallengers.filter(p => p.set_overdue).length;

    // The Needs attention strip: today's problems, each only when above zero.
    const today = periods?.periods?.today || {};
    const plural = (n, one, many) => `${num(n)} ${n === 1 ? one : many}`;
    const attention = [
        inboxWaiting > 0 && { key: 'inbox', tone: 'warn', go: () => reveal('emails'),
            label: plural(inboxWaiting, 'email waiting for you', 'emails waiting for you') },
        today.errors > 0 && { key: 'errors', tone: 'bad', go: () => reveal('errors'),
            label: plural(today.errors, 'error today', 'errors today') },
        today.emails_failed > 0 && { key: 'failed', tone: 'bad', go: () => reveal('emails'),
            label: plural(today.emails_failed, 'email failed to send today', 'emails failed to send today') },
        chasing > 0 && { key: 'chasing', tone: 'warn', go: () => setTab('challenge'),
            label: plural(chasing, 'challenger needs chasing', 'challengers need chasing') },
    ].filter(Boolean);
    const stillChecking = !periods || checking || !challenge;

    return (
        <div className="min-h-screen bg-background px-4 py-10 max-w-6xl mx-auto">
            <AdminNav />
            <div className="flex items-baseline justify-between gap-3 flex-wrap">
                <h1 className="text-2xl font-bold text-foreground">Mimique Dashboard</h1>
                <span className="text-xs text-stone-400">
                    {fmtDate(data.generated_at)}{' · '}
                    <button onClick={load} className="text-primary underline">Refresh</button>
                </span>
            </div>

            {/* Red alert: a paid service is refusing calls (credit run out, key
                revoked). On every tab, until marked sorted or a day passes
                with no new failure. */}
            {(errors?.outages || []).map(o => (
                <div key={o.name} className="mt-4 rounded-xl border border-rose-500 bg-rose-500 px-4 py-3 text-white">
                    <p className="font-bold">{o.service} is refusing calls</p>
                    <p className="mt-1 text-sm">
                        {num(o.count)} {o.count === 1 ? 'failure' : 'failures'} since {fmtDate(o.first)}, last at {fmtDate(o.last)}.
                        {o.fix && <> Usually the credit has run out or the key has stopped working. Check {o.fix}.</>}
                    </p>
                    <p className="mt-1 text-xs break-words opacity-90">{o.message}</p>
                    <button type="button" onClick={() => resolveError(o.name)}
                            className="mt-2 rounded-lg bg-white px-3 py-1 text-sm font-medium text-rose-700 hover:opacity-80">
                        Mark as sorted
                    </button>
                </div>
            ))}

            {/* Early warning: an allowance is 80% used this month. */}
            {(spend?.quota_warnings || []).map(w => (
                <div key={w.service} className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-700">
                    <p className="font-bold">{w.service} is {w.pct}% used this month</p>
                    <p className="mt-1 text-sm">
                        {w.detail}.{w.resets_at && <> Resets on {fmtDate(w.resets_at)}.</>} To add more, check {w.fix}.
                    </p>
                </div>
            ))}

            {/* The pages. Scrolls sideways on a phone rather than wrapping. */}
            <nav className="mt-4 -mx-4 px-4 flex gap-1 overflow-x-auto border-b border-stone-200">
                {TABS.map(([key, label]) => (
                    <button key={key} type="button" onClick={() => setTab(key)}
                            className={`shrink-0 whitespace-nowrap px-3 py-2 text-sm font-medium border-b-2 -mb-px ${
                                tab === key ? 'border-primary text-primary' : 'border-transparent text-stone-500 hover:text-foreground'}`}>
                        {label}
                    </button>
                ))}
            </nav>

            {tab === 'overview' && (
                <>
                    {/* Only what needs doing, and only when it does. Always
                        today, whatever the switch below says. */}
                    <div className="mt-5">
                        <p className="text-xs uppercase tracking-wide text-stone-500 mb-2">Needs attention</p>
                        {attention.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                                {attention.map(a => (
                                    <button key={a.key} type="button" onClick={a.go}
                                            className={`rounded-xl border px-3 py-2 text-sm font-medium text-left hover:opacity-80 ${
                                                a.tone === 'bad' ? 'border-rose-200 bg-rose-50 text-rose-700'
                                                    : 'border-amber-200 bg-amber-50 text-amber-700'}`}>
                                        {a.label} →
                                    </button>
                                ))}
                            </div>
                        ) : (
                            <p className={`rounded-xl border px-3 py-2 text-sm ${stillChecking
                                ? 'border-stone-200 bg-white text-stone-500'
                                : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}>
                                {stillChecking ? 'Checking…' : 'Nothing needs you right now.'}
                            </p>
                        )}
                    </div>

                    <div className="mt-6 flex flex-wrap items-center gap-3">
                        <Segmented options={PERIODS} value={period} onChange={setPeriod} />
                        <span className="text-xs text-stone-500">
                            {period === 'today' ? 'Since midnight' : `Today and the ${period === 'week' ? '6' : '29'} days before`}
                        </span>
                    </div>
                    {/* Every box: the number for the chosen window, the other
                        two windows underneath, tap for who or what. */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
                        {PERIOD_BOXES.map(b => {
                            const fmt = b.fmt || num;
                            const show = (x) => (!periods ? '…' : x == null || x.error ? '?' : fmt(x));
                            const v = periods?.periods?.[period]?.[b.key];
                            const n = v == null || v.error ? null : (b.amount ? b.amount(v) : v);
                            return (
                                <Stat key={b.key} label={b.label} onClick={() => b.go()} hint={false}
                                      value={show(v)}
                                      tone={n == null ? 'default'
                                          : b.tone === 'bad' ? (n > 0 ? 'bad' : 'good')
                                              : b.tone === 'warn' ? (n > 0 ? 'warn' : 'default')
                                                  : b.tone === 'good' && n > 0 ? 'good' : 'default'} />
                            );
                        })}
                    </div>
                    {periods?.fixed_monthly_eur > 0 && (
                        <p className="text-xs text-stone-400 mt-2">
                            API spend is what was used. The fixed fees, {eur(periods.fixed_monthly_eur)} a month, are on the Money tab.
                        </p>
                    )}

                </>
            )}
            {tab === 'users' && (
                <>

            <Section title="Users" id="users">
                {!people ? <p className="text-sm text-stone-500 mt-3">Loading…</p> : (
                    <div className="mt-3">
                        <p className="text-xs uppercase tracking-wide text-stone-500 mb-1">
                            Busiest first · {num(people.matching)} {people.matching === 1 ? 'person' : 'people'} in all
                            {people.shown < people.matching ? `, showing ${num(people.shown)}` : ''}
                        </p>
                        <UsersTable users={people.users} who={who} setWho={setWho} when={when} setWhen={setWhen}
                                    period={period} setPeriod={setPeriod}
                                    onSetComp={setComp} novemberEmails={novemberEmails} />
                    </div>
                )}
            </Section>

            {/* Did they come BACK. One visit has converted nobody; 44% of the
                people who practise a week end up paying. So this is the number
                to watch after a push, not the signup count. */}
            <Section title="New signups" id="funnel"
                     note="By the week they arrived. Before the 15 August launch is left out: those people met a half-built app.">
                {!ops ? <p className="text-sm text-stone-500">Loading…</p>
                    : funnel?.error ? <p className="text-sm text-rose-600">Could not read signups: {funnel.error}</p>
                        : !funnel?.length ? <p className="text-sm text-stone-500">Nobody has signed up since the launch window started.</p>
                            : (
                                <div className="overflow-x-auto rounded-xl border border-stone-200 bg-white">
                                    <table className="w-full text-xs">
                                        <thead>
                                            <tr className="border-b border-stone-100">
                                                <th className="px-3 py-2 text-left font-semibold text-stone-500">Week of</th>
                                                <th className="px-3 py-2 text-right font-semibold text-stone-500">Signed up</th>
                                                <th className="px-3 py-2 text-right font-semibold text-stone-500">Practised</th>
                                                <th className="px-3 py-2 text-right font-semibold text-stone-500 whitespace-nowrap">Came back</th>
                                                <th className="px-3 py-2 text-right font-semibold text-stone-500 whitespace-nowrap">7+ days</th>
                                                <th className="px-3 py-2 text-right font-semibold text-stone-500">Paid</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {funnel.map(c => (
                                                <tr key={c.week} className="border-b border-stone-50 last:border-0 hover:bg-stone-50">
                                                    <td className="px-3 py-2 whitespace-nowrap">{fmtDay(c.week)}</td>
                                                    <td className="px-3 py-2 text-right tabular-nums">{num(c.signups)}</td>
                                                    <td className="px-3 py-2 text-right tabular-nums text-stone-600">{num(c.practised)}</td>
                                                    {/* The one that predicts money. */}
                                                    <td className={`px-3 py-2 text-right tabular-nums font-semibold ${
                                                        c.returned ? 'text-emerald-600' : 'text-stone-400'}`}>
                                                        {num(c.returned)}
                                                    </td>
                                                    <td className="px-3 py-2 text-right tabular-nums text-stone-600">{num(c.stuck)}</td>
                                                    <td className="px-3 py-2 text-right tabular-nums text-stone-600">{num(c.paid)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                <p className="text-xs text-stone-500 mt-1.5">
                    "Came back" means they practised on a second day. Nobody who came once has
                    ever paid; of those who practised seven days or more, 44% do. It is the
                    column to watch after a push.
                </p>
            </Section>
                </>
            )}
            {tab === 'challenge' && (() => {
                // One person picked: their page, nothing else.
                const picked = pickedChallenger && challengers.find(p =>
                    p.email === pickedChallenger.email && p.started === pickedChallenger.started);
                if (picked) {
                    return (
                        <ChallengeDetail p={picked} length={challenge.length_days}
                                         target={challenge.daily_minutes} onSet={setChallenge}
                                         archived={isArchived(picked, cohort?.starts)}
                                         reminders={remindersByEmail[(picked.email || '').toLowerCase()]}
                                         onBack={() => setPickedChallenger(null)} />
                    );
                }
                const pick = (p) => { setPickedChallenger({ email: p.email, started: p.started }); window.scrollTo({ top: 0 }); };
                const table = (people) => (
                    <ChallengeTable people={people} length={challenge.length_days}
                                    target={challenge.daily_minutes} reminders={remindersByEmail} onPick={pick} />
                );
                return (
                    <>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
                            <Stat label="Taking part now" value={challenge ? num(activeChallengers.length) : '…'} />
                            <Stat label="On track" value={challenge ? num(onTrack) : '…'}
                                  tone={challenge && activeChallengers.length && onTrack === activeChallengers.length ? 'good' : 'default'}
                                  sub="not missed a day" />
                            <Stat label="Need chasing" value={challenge ? num(chasing) : '…'}
                                  tone={chasing > 0 ? 'bad' : 'default'}
                                  sub="tailored set overdue" />
                            <Stat label="November places" value={cohort ? num(cohort.spots_taken) : '…'}
                                  sub={cohort ? `of ${num(cohort.spots_total)}` : null} />
                        </div>

                        {challenge && <ChallengeGroup people={activeChallengers} />}

                        {!challenge ? <p className="text-sm text-stone-500 mt-5">Loading…</p>
                            : !activeChallengers.length
                                ? <p className="text-sm text-stone-500 mt-5">Nobody is taking part right now.</p>
                                // Grouped by start, so the November group reads on its own.
                                // Needs chasing first, then the most days missed. Tap a name.
                                : challengeGroups(activeChallengers, cohort?.starts).map(g => (
                                    <Section key={g.key} id={g.key === 'nov' ? 'challenge' : undefined}
                                             title={`${g.label} · ${num(g.people.length)}`}>
                                        {table(g.people)}
                                    </Section>
                                ))}

                        {archivedChallengers.length > 0 && (
                            <div className="mt-6">
                                <button type="button" onClick={() => setShowArchive(v => !v)}
                                        className="text-xs uppercase tracking-wide text-stone-500 underline">
                                    {showArchive ? 'Hide' : 'Show'} earlier challenges · {num(archivedChallengers.length)}
                                </button>
                                {showArchive && archiveMonths(archivedChallengers).map(([month, people]) => (
                                    <Section key={month} title={`${month} challenge · ${num(people.length)}`}>
                                        {table(people)}
                                    </Section>
                                ))}
                            </div>
                        )}

                        {cohort && (
                            <div className="mt-6">
                                <CohortBox cohort={cohort} onAdd={addToCohort} />
                            </div>
                        )}
                    </>
                );
            })()}
            {tab === 'money' && (
                <>

            <Section title="Money" id="money">
                {revenue?.configured && !revenue.error && (
                    <div className="mb-3">
                        <List>
                            <Row left="Customers paid" sub={`${revenue.charges} payments this month`}
                                 right={money(revenue.gross, revenue.currency)} />
                            <Row left="Stripe's fees" right={`- ${money(revenue.fees, revenue.currency)}`} />
                            <Row left="Yours, after fees" sub="What the month is actually worth"
                                 right={money(revenue.net, revenue.currency)} />
                            <Row left="Landed in your bank" sub="Payouts Stripe has sent or is sending"
                                 right={money(revenue.paid_out, revenue.currency)} />
                        </List>
                    </div>
                )}
                {revenue?.error && <p className="text-xs text-stone-500 mb-3">Stripe: {revenue.error}</p>}

                <div className="mb-3">
                    <button onClick={checkStripe} disabled={stripeChecking}
                            className="rounded-xl border border-stone-200 bg-white px-4 py-2 text-sm font-medium text-foreground hover:bg-stone-50 disabled:opacity-60">
                        {stripeChecking ? 'Checking…' : 'Check my payers against Stripe'}
                    </button>
                </div>

                {stripeCheck?.error && <p className="text-sm text-rose-600 mb-3">{stripeCheck.error}</p>}

                {stripeCheck && !stripeCheck.error && (
                    <div className="mb-4">
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                            <Stat label="Live in Stripe" value={num(stripeCheck.stripe_live_subscriptions)}
                                  sub="active, trialing or past due" />
                            <Stat label="Paid here" value={num(stripeCheck.db_paid)}
                                  tone={stripeCheck.db_paid === stripeCheck.stripe_live_subscriptions ? 'good' : 'bad'} />
                            <Stat label="Agree" value={num(stripeCheck.agree)}
                                  tone={stripeCheck.agree === stripeCheck.stripe_live_subscriptions ? 'good' : 'warn'} />
                        </div>

                        {stripeCheck.no_account.length > 0 && (
                            <div className="mt-3">
                                <p className="text-xs uppercase tracking-wide text-rose-600 mb-1">
                                    Paying Stripe with no Mimique account
                                </p>
                                <List>
                                    {stripeCheck.no_account.map(r => (
                                        <Row key={r.subscription_id} left={r.email || r.customer_id}
                                             tone="bad" sub={`${r.plan} · ${r.status} · since ${fmtDay(r.started)}`}
                                             right={r.name} />
                                    ))}
                                </List>
                            </div>
                        )}

                        {stripeCheck.wrong_plan.length > 0 && (
                            <div className="mt-3">
                                <p className="text-xs uppercase tracking-wide text-rose-600 mb-1">
                                    On the wrong plan here
                                </p>
                                <List>
                                    {stripeCheck.wrong_plan.map(r => (
                                        <Row key={r.subscription_id} left={r.account_email} tone="bad"
                                             sub={`Stripe says ${r.plan}, we say ${r.account_plan} · matched by ${r.linked_by}`}
                                             right={r.status} />
                                    ))}
                                </List>
                            </div>
                        )}

                        {stripeCheck.paid_without_subscription.length > 0 && (
                            <div className="mt-3">
                                <p className="text-xs uppercase tracking-wide text-amber-700 mb-1">
                                    Paid here, nothing live in Stripe
                                </p>
                                <List>
                                    {stripeCheck.paid_without_subscription.map(r => (
                                        <Row key={r.email} left={r.email} sub="cancelled, or never linked"
                                             right={r.plan} />
                                    ))}
                                </List>
                            </div>
                        )}

                        {stripeCheck.no_account.length === 0
                            && stripeCheck.wrong_plan.length === 0
                            && stripeCheck.paid_without_subscription.length === 0 && (
                            <p className="text-sm text-emerald-700 mt-3">
                                Stripe and Mimique agree on every subscription. Nobody is paying for
                                something they have not got.
                            </p>
                        )}
                        <p className="text-xs text-stone-400 mt-2">Checked {fmtDate(stripeCheck.checked_at)}.</p>
                    </div>
                )}
                <p className="text-xs text-stone-400">
                    {num(users.paid)} paying, {users.conversion_pct}% of everyone.{' '}
                    <button type="button" onClick={() => showUsers('paid')} className="text-primary underline">
                        Who they are, and when each upgraded →
                    </button>
                </p>
            </Section>

            <Section title="Costs" id="costs"
                     note={spend ? `Estimated, this calendar month (${spend.month})` : 'Estimated, this calendar month'}>
                {!ops ? <p className="text-sm text-stone-500">Loading…</p>
                    : spend?.error ? <p className="text-sm text-rose-600">Could not read spend: {spend.error}</p>
                        : (
                            <>
                                <div className="grid grid-cols-2 gap-3">
                                    <Stat label="Month to date" value={eur(spend.total_eur)} />
                                    <Stat label="Today" value={eur(spend.today_eur)} />
                                </div>

                                {spend.fixed?.length > 0 && (
                                    <div className="mt-3">
                                        <p className="text-xs uppercase tracking-wide text-stone-500 mb-1">Fixed monthly</p>
                                        <List>
                                            {spend.fixed.map(f => (
                                                <Row key={f.label} left={f.label}
                                                     sub={f.usd ? 'Paid whether it is used or not' : 'Amount not set yet'}
                                                     tone={f.usd ? undefined : 'bad'}
                                                     right={f.usd ? eur(f.eur) : 'set me'} />
                                            ))}
                                        </List>
                                        {spend.fixed.some(f => !f.usd) && (
                                            <p className="text-xs text-stone-500 mt-1.5">
                                                Fill these in with <code className="bg-stone-100 px-1 rounded">COST_FIXED_MONTHLY</code> in
                                                Railway, as <code className="bg-stone-100 px-1 rounded">Railway hosting:5,Domain:1.24</code>.
                                                Anything left at zero is missing from the total.
                                            </p>
                                        )}
                                    </div>
                                )}

                                {spend.services.length === 0
                                    ? <p className="text-sm text-stone-500 mt-3">No paid API calls recorded yet this month.</p>
                                    : (
                                        <div className="mt-3">
                                            <List>
                                                {spend.services.map(s => (
                                                    <Row key={s.service}
                                                         left={SERVICE_LABELS[s.service] || s.service}
                                                         sub={<>
                                                             {num(s.calls)} calls · {num(Math.round(s.units))} {SERVICE_UNITS[s.service] || 'units'}
                                                             {' · '}<ProviderLink service={s.service} />
                                                         </>}
                                                         right={s.cost_eur ? eur(s.cost_eur) : (s.note || 'no unit price set')} />
                                                ))}
                                            </List>
                                            {spend.services.filter(s => s.labels?.length).map(s => (
                                                <div key={`${s.service}-labels`} className="mt-3">
                                                    <p className="text-xs uppercase tracking-wide text-stone-500 mb-1">
                                                        {SERVICE_LABELS[s.service] || s.service} by feature
                                                    </p>
                                                    <List>
                                                        {s.labels.map(l => (
                                                            <Row key={l.label} left={l.label}
                                                                 sub={`${num(l.calls)} calls`}
                                                                 right={eur(l.cost_eur)} />
                                                        ))}
                                                    </List>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                {spend.daily.length > 1 && (
                                    <div className="mt-3 rounded-xl border border-stone-200 bg-white p-4">
                                        <p className="text-xs uppercase tracking-wide text-stone-500 mb-2">Per day this month</p>
                                        <div className="flex items-end gap-1 h-16">
                                            {spend.daily.map(d => (
                                                <div key={d.date} className="flex-1 flex flex-col justify-end items-center gap-1"
                                                     title={`${d.date}: ${eur(d.cost_eur)}`}>
                                                    <div className="w-full bg-primary/70 rounded-sm"
                                                         style={{ height: `${(d.cost_eur / peakSpend) * 100}%`, minHeight: d.cost_eur ? 3 : 0 }} />
                                                    <span className="text-[9px] text-stone-400">{d.date.slice(8)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                {/* Usage, not cost, except for Gemini. The SpeechAce fee is the
                                    same $40 whoever uses it, so a euro figure per person there
                                    would look like money and be nothing of the kind. Gemini is
                                    charged per token, so its tokens and its cost sit together. */}
                                {spend.by_user?.length > 0 && (() => {
                                    const checksShown = spend.by_user.reduce((n, u) => n + u.speechace_checks, 0);
                                    const limit = spend.speechace?.included || 0;
                                    return (
                                        <div className="mt-4">
                                            <p className="text-xs uppercase tracking-wide text-stone-500 mb-1">
                                                Heaviest users this month ({spend.month})
                                            </p>
                                            {/* Scrolls inside its own box, like the users table. */}
                                            <div className="overflow-x-auto rounded-xl border border-stone-200 bg-white">
                                                <table className="w-full text-xs">
                                                    <thead>
                                                        <tr className="border-b border-stone-100">
                                                            <th className="px-3 py-2 text-left font-semibold text-stone-500">User</th>
                                                            <th className="px-3 py-2 text-right font-semibold text-stone-500 whitespace-nowrap">Gemini tokens</th>
                                                            <th className="px-3 py-2 text-right font-semibold text-stone-500 whitespace-nowrap">Gemini cost</th>
                                                            <th className="px-3 py-2 text-right font-semibold text-stone-500 whitespace-nowrap">SpeechAce checks</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {spend.by_user.map(u => (
                                                            <tr key={u.email} className="border-b border-stone-50 last:border-0 hover:bg-stone-50">
                                                                <td className="px-3 py-2">
                                                                    {/* Same badges as the users table, not a second
                                                                        way of saying the same thing. */}
                                                                    <span className="inline-flex items-center gap-1 flex-wrap">
                                                                        <span className="text-foreground">{u.email}</span>
                                                                        <PlanBadge plan={u.plan} />
                                                                        {u.founding && <FoundingBadge />}
                                                                    </span>
                                                                </td>
                                                                <td className="px-3 py-2 text-right tabular-nums text-stone-600">{num(u.gemini_tokens)}</td>
                                                                <td className="px-3 py-2 text-right tabular-nums text-stone-600">{u.gemini_eur ? eur(u.gemini_eur) : '<0.01'}</td>
                                                                <td className="px-3 py-2 text-right tabular-nums text-stone-600">{num(u.speechace_checks)}</td>
                                                            </tr>
                                                        ))}
                                                        {/* The checks total is the one worth adding up: they all
                                                            come out of the same monthly allowance. */}
                                                        <tr className="border-t border-stone-200 font-semibold">
                                                            <td className="px-3 py-2 text-stone-500">These {spend.by_user.length}</td>
                                                            <td className="px-3 py-2 text-right tabular-nums">
                                                                {num(spend.by_user.reduce((n, u) => n + u.gemini_tokens, 0))}
                                                            </td>
                                                            <td className="px-3 py-2 text-right tabular-nums">
                                                                {eur(spend.by_user.reduce((n, u) => n + u.gemini_eur, 0))}
                                                            </td>
                                                            <td className="px-3 py-2 text-right tabular-nums">
                                                                {num(checksShown)}
                                                                {limit > 0 && (
                                                                    <span className="ml-1 font-normal text-stone-500">
                                                                        · {Math.round(checksShown / limit * 100)}% of {num(limit)}
                                                                    </span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                            <p className="text-xs text-stone-500 mt-1.5">
                                                Money is Gemini only, the one thing charged per use. SpeechAce checks are
                                                counted, not priced: they come out of the {num(limit)} the plan already
                                                covers, and only cost anything past that.
                                                {spend.by_user.some(u => u.estimated) &&
                                                    ' Gemini input and output have only been recorded separately since 9 September, so earlier tokens are split at the ratio the newer ones show.'}
                                            </p>
                                        </div>
                                    );
                                })()}

                                <p className="text-xs uppercase tracking-wide text-stone-500 mt-4 mb-1">Allowances</p>
                                <div className="grid gap-3 mb-3">
                                    {spend.speechace && (
                                        <Quota label={spend.since
                                                   ? `SpeechAce requests counted since ${fmtDay(spend.since)}`
                                                   : 'SpeechAce requests counted so far'}
                                               used={spend.speechace.segments} limit={spend.speechace.included}
                                               note={<>
                                                   The allowance of {num(spend.speechace.included)} resets monthly, but we only
                                                   started counting on {fmtDay(spend.since)}, so this bar is not the full month yet.
                                                   For August so far, go by <ProviderLink service="speechace" />
                                                   {spend.speechace.over > 0
                                                       ? ` ${num(spend.speechace.over)} over the allowance, charged at $${spend.speechace.per_15s} each.`
                                                       : ` Anything past the allowance costs $${spend.speechace.per_15s} per 15 seconds.`}
                                               </>} />
                                    )}
                                </div>
                                <div className="grid gap-3">
                                    {el?.configured && !el.error && (
                                        <Quota label="ElevenLabs credits (grant)"
                                               used={el.used} limit={el.limit}
                                               note={<>
                                                   {num(elLeft)} left. Nothing to pay until these run out.
                                                   {runwayText ? ` At ${num(Math.round(elPerDay))} characters a day, that lasts ${runwayText.startsWith('far') ? runwayText : `roughly ${runwayText}`}.` : ''}
                                                   {el.resets_at ? ` Counter resets ${fmtDay(el.resets_at)}.` : ''}
                                               </>} />
                                    )}
                                    {el?.error && <p className="text-xs text-stone-500">ElevenLabs credits: {el.error}</p>}
                                    {emails && !emails.error && (
                                        <Quota label={emails.since
                                                   ? `Resend emails counted since ${fmtDay(emails.since)}`
                                                   : 'Resend emails counted so far'}
                                               used={emails.this_month} limit={emails.monthly_quota}
                                               note={<>
                                                   The free allowance of {num(emails.monthly_quota)} resets monthly. Emails sent
                                                   before {fmtDay(emails.since)} were never logged here, so for the full month
                                                   check <ProviderLink service="resend" />
                                               </>} />
                                    )}
                                    {ml?.configured && (ml.unique_subscribers ?? ml.total_subscribers) != null && (
                                        <Quota label="MailerLite subscribers"
                                               used={ml.unique_subscribers ?? ml.total_subscribers}
                                               limit={ml.quota || 250} />
                                    )}
                                </div>
                            </>
                        )}
            </Section>

            {/* Who is leaving, and what they said on the way out. The reasons
                are typed by the person cancelling, so they are the one place
                the app says why it is losing people. */}
            <Section title="Cancellations" id="cancellations"
                     note="Cancelling ends the plan at the end of the paid period, so someone here keeps their plan until the date shown.">
                {!cancels?.rows?.length ? (
                    <p className="text-sm text-stone-500">Nobody has cancelled yet.</p>
                ) : (
                    <>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                            <Stat label="Last 30 days" value={num(cancels.last_30d)}
                                  tone={cancels.last_30d > 0 ? 'warn' : 'good'} />
                            <Stat label="Still paid up" value={num(cancels.pending)}
                                  sub="Plan runs to the date below" />
                            <Stat label="Changed their mind" value={num(cancels.resumed)}
                                  tone={cancels.resumed > 0 ? 'good' : 'default'} />
                        </div>

                        {cancels.reasons?.length > 0 && (
                            <div className="mt-3">
                                <p className="text-xs uppercase tracking-wide text-stone-500 mb-1">Why they left</p>
                                <List>
                                    {cancels.reasons.map(([reason, count]) => (
                                        <Row key={reason} left={reason} right={num(count)} />
                                    ))}
                                </List>
                            </div>
                        )}

                        <div className="mt-3">
                            <List>
                                {cancels.rows.map(c => (
                                    <Row
                                        key={`${c.email}-${c.created_at}`}
                                        left={`${c.name || c.email}${c.founding_member ? ' · founding' : ''}`}
                                        sub={[
                                            c.email,
                                            c.reason || 'no reason given',
                                            c.detail ? `“${c.detail}”` : null,
                                            c.resumed_at ? 'Came back' : null,
                                        ].filter(Boolean).join(' · ')}
                                        tone={c.resumed_at ? 'default' : 'bad'}
                                        right={`${(c.plan || '').toUpperCase()}\n${fmtDay(c.created_at)}${c.ends_at ? `\nto ${fmtDay(c.ends_at)}` : ''}`}
                                    />
                                ))}
                            </List>
                        </div>
                    </>
                )}
            </Section>
                </>
            )}
            {tab === 'health' && (
                <>

            <Section title="Errors" id="errors"
                     note={errors?.retention_days
                ? `Everything the backend and the app report, kept for ${errors.retention_days} days`
                : 'Everything the backend and the app report'}>
                {!ops ? <p className="text-sm text-stone-500">Loading…</p>
                    : errors?.error ? <p className="text-sm text-rose-600">Could not read the error log: {errors.error}</p>
                        : errors.recent.length === 0
                            ? <p className="text-sm text-stone-500">Nothing has broken.</p>
                            : (
                                <>
                                    <p className="text-xs uppercase tracking-wide text-stone-500 mb-1">
                                        Most common, last 7 days · tap one to see what to do
                                    </p>
                                    <List>
                                        {errors.top.map(t => (
                                            <ExpandableRow key={`${t.source}:${t.name}`}
                                                           title={t.name}
                                                           tone={t.count > 5 ? 'bad' : undefined}
                                                           sub={`${t.source} · last ${fmtDate(t.last)}`}
                                                           right={`${t.count}×`}>
                                                {/* Who it happened to, where and on what. A count
                                                    on its own could not tell one person's bad
                                                    afternoon from everybody hitting a wall. */}
                                                <div className="rounded-lg bg-stone-50 px-3 py-2 space-y-0.5">
                                                    <p className="text-stone-600">
                                                        <span className="font-semibold">Who: </span>
                                                        {t.users?.length
                                                            ? `${t.users.join(', ')}${t.count > t.users.length ? ` (${t.users.length} ${t.users.length === 1 ? 'person' : 'people'})` : ''}`
                                                            : 'Nobody signed in, or their session had already gone'}
                                                    </p>
                                                    {t.pages?.length > 0 && (
                                                        <p className="text-stone-600">
                                                            <span className="font-semibold">Where: </span>{t.pages.join(', ')}
                                                        </p>
                                                    )}
                                                    {t.devices?.length > 0 && (
                                                        <p className="text-stone-600">
                                                            <span className="font-semibold">On: </span>{t.devices.join(', ')}
                                                        </p>
                                                    )}
                                                    {t.first && t.first !== t.last && (
                                                        <p className="text-stone-600">
                                                            <span className="font-semibold">Between: </span>
                                                            {fmtDate(t.first)} and {fmtDate(t.last)}
                                                        </p>
                                                    )}
                                                </div>
                                                <Help name={t.name} />
                                                <button type="button" onClick={() => resolveError(t.name)}
                                                        className="rounded-lg border border-stone-200 bg-white px-3 py-1 text-xs font-medium hover:bg-stone-50">
                                                    Mark as sorted
                                                </button>
                                            </ExpandableRow>
                                        ))}
                                    </List>
                                    {errors.resolved?.length > 0 && (
                                        <p className="text-xs text-stone-400 mt-2">
                                            Hidden as sorted:{' '}
                                            {errors.resolved.map((r, n) => (
                                                <span key={r.name}>
                                                    {n > 0 && ', '}
                                                    <button type="button" onClick={() => resolveError(r.name, true)}
                                                            className="underline hover:text-foreground">
                                                        {r.name}
                                                    </button>
                                                </span>
                                            ))}
                                        </p>
                                    )}
                                    <p className="text-xs uppercase tracking-wide text-stone-500 mt-4 mb-1">Latest</p>
                                    <List>
                                        {errors.recent.map((e, i) => (
                                            <ExpandableRow key={i} title={e.name} sub={e.message}
                                                           right={`${e.source}\n${fmtDate(e.at)}`}>
                                                <Help name={e.name} />
                                                <button type="button" onClick={() => resolveError(e.name)}
                                                        className="rounded-lg border border-stone-200 bg-white px-3 py-1 text-xs font-medium hover:bg-stone-50">
                                                    Mark as sorted
                                                </button>
                                                {e.message && (
                                                    <p className="text-stone-500 break-words">
                                                        <span className="font-semibold">Message: </span>{e.message}
                                                    </p>
                                                )}
                                                {Object.keys(e.props || {}).length > 0 && (
                                                    <div className="rounded-lg bg-stone-50 px-3 py-2 space-y-0.5">
                                                        {Object.entries(e.props).map(([k, v]) => (
                                                            <p key={k} className="text-stone-500 break-words">{k}: {v}</p>
                                                        ))}
                                                    </div>
                                                )}
                                                <p className="text-stone-400">{fmtDate(e.at)} · reported by the {e.source}</p>
                                            </ExpandableRow>
                                        ))}
                                    </List>
                                </>
                            )}
            </Section>

            <Section title="Email" id="emails">
                <p className="text-xs uppercase tracking-wide text-stone-500 mb-1">Waiting for you</p>
                <button onClick={checkInbox} disabled={checking}
                        className="rounded-xl border border-stone-200 bg-white px-4 py-2 text-sm font-medium text-foreground hover:bg-stone-50 disabled:opacity-60">
                    {checking ? 'Checking…' : 'Check for new email'}
                </button>

                {inbox && (
                    <div className="mt-3 space-y-3">
                        {inbox.mailboxes.map(m => (
                            <div key={m.label}>
                                <p className="text-xs uppercase tracking-wide text-stone-500 mb-1">
                                    {m.label}{m.address ? ` · ${m.address}` : ''}
                                </p>
                                {!m.configured ? (
                                    <p className="text-sm text-stone-500">
                                        Not connected yet. Add its details in Railway and this fills in.
                                    </p>
                                ) : m.error ? (
                                    <p className="text-sm text-rose-600">{m.error}</p>
                                ) : m.unread === 0 ? (
                                    <p className="text-sm text-stone-500">Nothing waiting.</p>
                                ) : (
                                    <>
                                        <p className="text-sm text-foreground mb-1">
                                            <span className="font-semibold">{num(m.unread)}</span> to look at
                                            {m.label === 'Gmail' && m.unread_total > m.unread
                                                ? ` (of ${num(m.unread_total)} unread in total)`
                                                : ''}
                                        </p>
                                        <List>
                                            {m.messages.map((msg, i) => (
                                                <Row key={i} left={msg.subject}
                                                     sub={msg.why ? `${msg.from} · ${msg.why}` : msg.from}
                                                     right={msg.date.slice(0, 16)} />
                                            ))}
                                        </List>
                                    </>
                                )}
                            </div>
                        ))}
                        <p className="text-xs text-stone-400">Checked {fmtDate(inbox.checked_at)}.</p>
                    </div>
                )}

                <p className="text-xs uppercase tracking-wide text-stone-500 mt-6 mb-1">Sent by Mimique</p>
                <p className="text-xs text-stone-500 mb-2">
                    Practice reminders {reminders.schedule}, {num(reminders.opted_in)} switched on.
                    {reminders.last_run
                        ? ` Last run ${fmtDate(reminders.last_run.ran_at)}: sent ${num(reminders.last_run.sent)},
                            skipped ${num(reminders.last_run.skipped_practised)}, failed ${num(reminders.last_run.failed)}.`
                        : ' Never run yet.'}
                </p>
                {!ops ? <p className="text-sm text-stone-500">Loading…</p>
                    : emails?.error ? <p className="text-sm text-rose-600">Could not read the email log: {emails.error}</p>
                        : (
                            <>
                                {!emails.configured && (
                                    <p className="text-sm text-rose-600 mb-3">
                                        RESEND_API_KEY is not set, so nothing is being sent at all.
                                    </p>
                                )}
                                {emails.by_kind.length === 0
                                    ? <p className="text-sm text-stone-500">No emails sent in the last 7 days.</p>
                                    : (
                                        <List>
                                            {emails.by_kind.map(k => (
                                                <Row key={k.kind} left={k.kind}
                                                     tone={k.failed > 0 ? 'bad' : undefined}
                                                     right={`${k.sent} sent${k.failed ? ` · ${k.failed} failed` : ''}`} />
                                            ))}
                                        </List>
                                    )}
                                {emails.recent.length > 0 && (
                                    <>
                                        <p className="text-xs uppercase tracking-wide text-stone-500 mt-4 mb-1">Latest</p>
                                        <List>
                                            {emails.recent.map((e, i) => (
                                                <Row key={i}
                                                     left={e.to}
                                                     tone={e.ok ? undefined : 'bad'}
                                                     sub={`${e.kind} · ${e.subject}${e.error ? ` · ${e.error}` : ''}`}
                                                     right={`${e.ok ? 'sent' : 'FAILED'}\n${fmtDate(e.at)}`} />
                                            ))}
                                        </List>
                                    </>
                                )}
                            </>
                        )}
            </Section>

            <Section title="MailerLite" id="mailerlite">
                {!ml.configured ? (
                    <p className="text-sm text-stone-500">
                        Not connected. Add <code className="text-xs bg-stone-100 px-1 rounded">MAILERLITE_API_KEY</code> in
                        Railway and this section fills in.
                    </p>
                ) : ml.error ? (
                    <p className="text-sm text-rose-600">MailerLite error: {ml.error}</p>
                ) : (
                    <>
                        <div className="grid grid-cols-2 gap-3">
                            <Stat label="Subscribers" value={ml.total_subscribers ?? '—'} />
                            <Stat label="Groups" value={ml.groups?.length ?? 0} />
                        </div>
                        {ml.groups?.length > 0 && (
                            <div className="mt-3">
                                <List>
                                    {ml.groups.map(g => (
                                        <Row key={g.name} left={g.name} right={g.active} />
                                    ))}
                                </List>
                            </div>
                        )}
                        <div className="mt-3">
                            <button onClick={checkMailerlite} disabled={mlChecking}
                                    className="rounded-xl border border-stone-200 bg-white px-4 py-2 text-sm font-medium text-foreground hover:bg-stone-50 disabled:opacity-60">
                                {mlChecking ? 'Checking…' : 'Who never opens anything?'}
                            </button>
                        </div>

                        {mlDetail?.error && <p className="text-sm text-rose-600 mt-2">{mlDetail.error}</p>}

                        {mlDetail && !mlDetail.error && (
                            <div className="mt-3">
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                    <Stat label="Active" value={num(mlDetail.active)}
                                          sub={`of ${num(mlDetail.quota)} spots`} />
                                    <Stat label="Open your emails" value={num(mlDetail.engaged)} tone="good" />
                                    <Stat label="Never opened one" value={num(mlDetail.never_opened_count)}
                                          tone={mlDetail.never_opened_count > 0 ? 'warn' : 'default'}
                                          sub="Been sent at least one" />
                                    <Stat label="Spots free" value={num(mlDetail.spare)}
                                          tone={mlDetail.spare < 20 ? 'bad' : 'default'}
                                          sub={mlDetail.reclaimable
                                              ? `${num(mlDetail.spare + mlDetail.reclaimable)} if you cleared the dead ones`
                                              : null} />
                                </div>

                                {mlDetail.never_opened.length > 0 && (
                                    <div className="mt-3">
                                        <p className="text-xs uppercase tracking-wide text-stone-500 mb-1">
                                            Never opened, most ignored first
                                            {mlDetail.never_opened_count > mlDetail.never_opened.length
                                                ? ` · showing ${num(mlDetail.never_opened.length)} of ${num(mlDetail.never_opened_count)}`
                                                : ''}
                                        </p>
                                        <List>
                                            {mlDetail.never_opened.map(r => (
                                                <Row key={r.email} left={r.name || r.email}
                                                     sub={`${r.email} · joined ${fmtDay(r.subscribed_at)}`}
                                                     right={`${num(r.sent)} sent
0 opened`} />
                                            ))}
                                        </List>
                                    </div>
                                )}

                                {mlDetail.never_sent_count > 0 && (
                                    <p className="text-xs text-stone-500 mt-2">
                                        {num(mlDetail.never_sent_count)} more have never been sent anything yet, so there
                                        is nothing to judge them on. Leave those alone.
                                    </p>
                                )}
                                <p className="text-xs text-stone-400 mt-2">Checked {fmtDate(mlDetail.checked_at)}.</p>
                            </div>
                        )}

                        {ml.scheduled_campaigns?.length > 0 && (
                            <div className="mt-3">
                                <p className="text-xs uppercase tracking-wide text-stone-500 mb-1">Scheduled campaigns</p>
                                <List>
                                    {ml.scheduled_campaigns.map(c => (
                                        <Row key={c.name} left={c.name} right={fmtDate(c.scheduled_for)} />
                                    ))}
                                </List>
                            </div>
                        )}
                    </>
                )}
            </Section>
                </>
            )}
        </div>
    );
}
